import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ImageCarouselPreviewProps {
  settings: {
    images: string[];
    autoplay: boolean;
    interval: number;
    showArrows: boolean;
    showDots: boolean;
    height: string;
    borderRadius: string;
    objectFit: string;
  };
}

export const ImageCarouselPreview = ({ settings }: ImageCarouselPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { images, autoplay, interval, showArrows, showDots, height, borderRadius, objectFit } = settings;

  useEffect(() => {
    if (!autoplay || !images?.length) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoplay, interval, images?.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  if (!images?.length) {
    return (
      <div className="bg-muted p-12 text-center rounded-lg">
        <p className="text-muted-foreground">أضف صوراً إلى الكاروسيل</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden" style={{ borderRadius: `${borderRadius}px` }}>
      <div className="relative" style={{ height: `${height}px` }}>
        <img
          src={images[currentIndex]}
          alt={`Slide ${currentIndex + 1}`}
          className="w-full h-full transition-opacity duration-500"
          style={{ objectFit: objectFit as any }}
          loading="lazy"
          decoding="async"
        />
      </div>

      {showArrows && images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {showDots && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition ${
                currentIndex === index ? "bg-white w-6" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
